import { AuthSession, PatientHandoverItem, HandoverNote, OtpRequestResult, ActiveInpatient, NurseUser, DepartmentItem, WardServicePatientListFilter } from '../types';

export class VykoAPIClient {
  private baseUrl: string = 'https://vydehi.vykohms.com:1111';
  public sessionToken: string | null = null;
  public empId: string | number | null = null;
  public userId: string | number | null = null;
  public loginId: string | null = null;
  public userName: string | null = null;
  public name: string | null = null;
  public accountIdentifier: string = '';

  constructor(customBaseUrl?: string) {
    if (customBaseUrl) {
      this.baseUrl = customBaseUrl.replace(/\/$/, '');
    } else {
      this.baseUrl = 'https://vydehi.vykohms.com:1111';
    }
    this.restoreSession();
  }

  public setBaseUrl(url: string) {
    this.baseUrl = url.replace(/\/$/, '');
  }

  public getBaseUrl(): string {
    return this.baseUrl;
  }

  public static readonly SESSION_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

  private restoreSession(): boolean {
    try {
      const saved = localStorage.getItem('vyko_auth_session');
      if (saved) {
        const session: AuthSession = JSON.parse(saved);
        
        // Enforce 24-hour token expiration rule
        const savedAt = session.savedAt || (session.loginTimestamp ? new Date(session.loginTimestamp).getTime() : 0);
        if (savedAt && Date.now() - savedAt > VykoAPIClient.SESSION_TTL_MS) {
          console.warn('Session expired after 24 hours. Purging token from localStorage.');
          this.clearSession();
          return false;
        }

        this.sessionToken = session.sessionToken;
        this.empId = session.empId ?? null;
        this.userId = session.userId ?? null;
        this.loginId = session.loginId;
        this.userName = session.userName ?? session.name ?? null;
        this.name = session.name ?? session.userName ?? null;
        this.accountIdentifier = session.accountIdentifier;
        return true;
      }
    } catch (e) {
      console.error('Failed to restore session from localStorage', e);
    }
    return false;
  }

  public saveSession(session: AuthSession): void {
    if (!session.savedAt) {
      session.savedAt = Date.now();
    }
    if (!session.loginTimestamp) {
      session.loginTimestamp = new Date().toISOString();
    }
    this.sessionToken = session.sessionToken;
    this.empId = session.empId ?? null;
    this.userId = session.userId ?? null;
    this.loginId = session.loginId;
    this.userName = session.userName ?? session.name ?? null;
    this.name = session.name ?? session.userName ?? null;
    this.accountIdentifier = session.accountIdentifier;
    localStorage.setItem('vyko_auth_session', JSON.stringify(session));
  }

  public setUserName(name: string): void {
    if (!name || !name.trim()) return;
    const cleanName = name.trim().split(/\s+/)[0];
    this.userName = cleanName;
    this.name = cleanName;
    try {
      const saved = localStorage.getItem('vyko_auth_session');
      if (saved) {
        const session: AuthSession = JSON.parse(saved);
        session.userName = cleanName;
        session.name = cleanName;
        localStorage.setItem('vyko_auth_session', JSON.stringify(session));
      }
    } catch (e) {
      console.error('Failed to update session username', e);
    }
  }

  public getUserDisplayName(): string {
    const raw = this.userName || this.name;
    if (raw && raw.trim()) {
      const first = raw.trim().split(/\s+/)[0];
      return this.formatDisplayName(first);
    }
    if (this.loginId) {
      return this.formatDisplayName(this.loginId);
    }
    return 'Nurse';
  }

  private formatDisplayName(str: string): string {
    const cleaned = str.trim().split(/\s+/)[0] || '';
    if (!cleaned) return 'Nurse';
    // If it is in ALL CAPS, title-case to sentence format
    return cleaned.charAt(0).toUpperCase() + cleaned.slice(1).toLowerCase();
  }

  /**
   * Resolve user's actual display name from hospital network notes or confirmed handovers
   */
  public async resolveUserName(): Promise<string> {
    if (this.userName && this.userName.trim()) {
      return this.getUserDisplayName();
    }

    if (!this.loginId) {
      return 'Nurse';
    }

    // 1. Try checking pending handovers
    try {
      const pending = await this.fetchHandoverIds(1);
      if (pending && pending.length > 0) {
        for (const p of pending.slice(0, 3)) {
          const notes = await this.fetchHandover(String(p.hospitalNumber), String(p.ipNumber));
          for (const note of notes) {
            if (note.handeOverAssignedUserName) {
              const name = String(note.handeOverAssignedUserName).trim();
              if (name) {
                this.setUserName(name);
                return this.getUserDisplayName();
              }
            }
          }
        }
      }
    } catch (e) {
      console.warn('Could not resolve user name from pending handovers', e);
    }

    // 2. Try checking confirmed handovers (ackStatus: NTC0002)
    try {
      const confirmed = await this.fetchConfirmedHandovers(1);
      if (confirmed && confirmed.result && confirmed.result.length > 0) {
        for (const p of confirmed.result.slice(0, 3)) {
          const notes = await this.fetchHandover(String(p.hospitalNumber), String(p.ipNumber));
          for (const note of notes) {
            if (note.handeOverAssignedUserName) {
              const name = String(note.handeOverAssignedUserName).trim();
              if (name) {
                this.setUserName(name);
                return this.getUserDisplayName();
              }
            }
          }
        }
      }
    } catch (e) {
      console.warn('Could not resolve user name from confirmed handovers', e);
    }

    return this.getUserDisplayName();
  }

  public clearSession(): void {
    this.sessionToken = null;
    this.empId = null;
    this.userId = null;
    this.loginId = null;
    this.userName = null;
    this.name = null;
    this.accountIdentifier = '';
    localStorage.removeItem('vyko_auth_session');
  }

  public isAuthenticated(): boolean {
    if (!this.sessionToken || !this.loginId) return false;
    try {
      const saved = localStorage.getItem('vyko_auth_session');
      if (saved) {
        const s: AuthSession = JSON.parse(saved);
        const savedAt = s.savedAt || (s.loginTimestamp ? new Date(s.loginTimestamp).getTime() : 0);
        if (savedAt && Date.now() - savedAt > VykoAPIClient.SESSION_TTL_MS) {
          this.clearSession();
          return false;
        }
      }
    } catch {
      return false;
    }
    return true;
  }

  public getSessionRemainingTimeMs(): number {
    try {
      const saved = localStorage.getItem('vyko_auth_session');
      if (saved) {
        const s: AuthSession = JSON.parse(saved);
        const savedAt = s.savedAt || (s.loginTimestamp ? new Date(s.loginTimestamp).getTime() : 0);
        if (savedAt) {
          const elapsed = Date.now() - savedAt;
          return Math.max(0, VykoAPIClient.SESSION_TTL_MS - elapsed);
        }
      }
    } catch {}
    return 0;
  }

  /**
   * Universal POST request adhering to strict header constraint:
   * Only 'authorization' and 'content-type: application/json'
   */
  private async _post<T = any>(endpoint: string, payload: Record<string, unknown>, retries = 2): Promise<{ status: number; data: T }> {
    const url = `${this.baseUrl}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;

    const headers: Record<string, string> = {
      'content-type': 'application/json',
    };

    if (this.sessionToken) {
      headers['authorization'] = this.sessionToken;
    }

    let lastError: any = null;

    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const res = await fetch(url, {
          method: 'POST',
          headers,
          body: JSON.stringify(payload),
        });

        let data: any = {};
        const text = await res.text();
        if (text) {
          try {
            data = JSON.parse(text);
          } catch {
            data = { raw: text };
          }
        }

        return { status: res.status, data };
      } catch (err) {
        lastError = err;
        if (attempt < retries) {
          await new Promise((r) => setTimeout(r, 1000));
          continue;
        }
      }
    }

    throw lastError || new Error(`Request to ${endpoint} failed after ${retries + 1} attempts`);
  }

  /**
   * Request OTP for email or mobile
   */
  public async requestOtp(mobileOrEmail: string): Promise<OtpRequestResult> {
    const payload = {
      email: mobileOrEmail,
      phoneNumber: null,
    };

    const endpoint = '/api-v2/otp-login';

    try {
      const { status, data } = await this._post(endpoint, payload);

      if (status !== 200) {
        return {
          success: false,
          isOtpRequired: true,
          message: data?.message || `Server responded with status ${status}`,
        };
      }

      if (data.error === false) {
        const res = (data.result as Record<string, unknown>) || {};
        
        // Check both isenableotp and isOtpSent flags from api-v2/otp-login
        // If isenableotp === 0 or isOtpSent === 0, account uses password authentication only
        let isenableotp = 1;
        if (res.isenableotp !== undefined && res.isenableotp !== null) {
          isenableotp = (res.isenableotp === 1 || res.isenableotp === '1' || res.isenableotp === true) ? 1 : 0;
        }
        let isOtpSent = 1;
        if (res.isOtpSent !== undefined && res.isOtpSent !== null) {
          isOtpSent = (res.isOtpSent === 1 || res.isOtpSent === '1' || res.isOtpSent === true) ? 1 : 0;
        }

        const isOtpRequired = isenableotp === 1 && isOtpSent === 1;

        if (res.firstName) {
          const preName = String(res.firstName).trim().split(/\s+/)[0];
          if (preName) {
            this.setUserName(preName);
          }
        }

        if (res.loginId) {
          this.loginId = String(res.loginId);
        }

        const firstName = res.firstName ? String(res.firstName).trim().split(/\s+/)[0] : '';
        const defaultMsg = isOtpRequired
          ? (data.message || 'OTP has been sent to your registered mobile/email for verification.')
          : (firstName
              ? `Welcome ${firstName}! OTP is not required for your account. Please enter your password to sign in.`
              : 'OTP is not required for this account. Please enter your password to sign in.');

        return {
          success: true,
          isOtpRequired,
          firstName: res.firstName as string | undefined,
          loginId: res.loginId as string | undefined,
          mobileNumber: res.mobileNumber as string | undefined,
          message: isOtpRequired ? (data.message || defaultMsg) : defaultMsg,
          rawResult: res,
        };
      }

      // Check if backend message indicates OTP is disabled or not required
      if (data?.message && /not\s+enabled|disabled|not\s+required/i.test(data.message)) {
        return {
          success: true,
          isOtpRequired: false,
          message: data.message,
        };
      }

      return {
        success: false,
        isOtpRequired: true,
        message: data?.message || 'Failed to send OTP. Please check the email/number.',
      };
    } catch (e: any) {
      return {
        success: false,
        isOtpRequired: true,
        message: e?.message || 'Network error requesting OTP',
      };
    }
  }

  /**
   * Login with mobile/email, password and optional OTP (if required)
   */
  public async login(
    mobileOrEmail: string,
    password = '',
    otp?: string | null
  ): Promise<{ success: boolean; message: string; session?: AuthSession }> {
    const cleanOtp = otp && otp.trim() ? otp.trim() : null;
    const payload: Record<string, unknown> = {
      email: mobileOrEmail,
      password,
      applicationCode: 'LOGRO0003',
      firstTry: true,
      otp: cleanOtp,
    };

    const endpoint = '/api-v2/user/login';

    try {
      const { status, data } = await this._post(endpoint, payload);

      if (status !== 200) {
        return {
          success: false,
          message: data?.message || `Login failed with status ${status}`,
        };
      }

      if (
        data.error === false &&
        (data.accessToken ||
          data.message === 'User Logged In Successfully' ||
          (typeof data.message === 'string' && data.message.toLowerCase().includes('success')))
      ) {
        const userRes = data.result || {};
        const savedRoutes = data.savedRoutes || {};

        let sessionToken = data.accessToken;
        let empId = userRes.employeeId || data.employeeId || null;
        let userId = userRes.id || data.userId || null;
        let loginId = savedRoutes.userId || userRes.loginId || data.loginId || null;

        const firstName = (userRes.firstName || '').trim();
        let name = firstName;

        if (!name) {
          const raw = (data.userName || data.name || data.employeeName || data.firstName || data.fullName || '') as string;
          name = raw.trim().split(/\s+/)[0] || '';
        }

        // Fallback to decode JWT claims if loginId, userId or name is missing
        if (sessionToken && (!loginId || !userId || !name)) {
          try {
            const parts = sessionToken.split('.');
            if (parts.length > 1) {
              const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
              const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
              const claims = JSON.parse(atob(padded));
              if (!loginId) loginId = claims.loginId;
              if (!userId) userId = claims.id;
              if (!name) name = claims.name || claims.userName || claims.employeeName || claims.sub;
            }
          } catch (jwtErr) {
            console.warn('Could not decode JWT payload', jwtErr);
          }
        }

        const session: AuthSession = {
          sessionToken,
          empId,
          userId,
          loginId: loginId || mobileOrEmail,
          userName: name || null,
          name: name || null,
          accountIdentifier: mobileOrEmail,
        };

        this.saveSession(session);

        return {
          success: true,
          message: 'Logged in successfully',
          session,
        };
      }

      return {
        success: false,
        message: data?.message || 'Invalid credentials or OTP',
      };
    } catch (e: any) {
      return {
        success: false,
        message: e?.message || 'Network error during login',
      };
    }
  }

  /**
   * Universal fetch for handed-to-me queue
   * ackStatus:
   *   'NTC0001' = Pending handovers to acknowledge
   *   'NTC0002' = Confirmed / Accepted handovers
   */
  public async fetchHandedToMe(
    ackStatus: 'NTC0001' | 'NTC0002' = 'NTC0001',
    page = 1,
    limit = 10
  ): Promise<{ result: PatientHandoverItem[]; pageCount: number }> {
    if (!this.loginId) {
      throw new Error('Not authenticated');
    }

    const payload = {
      organizationId: 1,
      businessId: 1,
      groupId: 1,
      userId: this.loginId,
      page,
      limit,
      pageSize: limit,
      ackStatus,
    };

    const endpoint = '/api-v2/doctor-desk-mobile/handed-to-me';

    const { status, data } = await this._post(endpoint, payload);

    if (status !== 200) {
      throw new Error(data?.message || `Failed to fetch handovers (${ackStatus}): status ${status}`);
    }

    const rawResult = (data?.result as PatientHandoverItem[]) || [];
    const result = rawResult.map((p) => ({
      ...p,
      arrivalStatus: p.arrivalStatus !== undefined ? p.arrivalStatus : (p.doa ? 1 : 0),
    }));
    const pageCount = typeof data?.pageCount === 'number'
      ? data.pageCount
      : Number(data?.pageCount || 0);

    return { result, pageCount };
  }

  /**
   * Fetch pending handed-to-me patient items (ackStatus: 'NTC0001')
   * Automatically fetches all pages so all pending patients are shown and accepted, not just 10.
   */
  public async fetchHandoverIds(specificPage?: number): Promise<PatientHandoverItem[]> {
    if (typeof specificPage === 'number' && specificPage > 0) {
      const data = await this.fetchHandedToMe('NTC0001', specificPage);
      return data.result;
    }

    // 1. Fetch first page
    const firstPage = await this.fetchHandedToMe('NTC0001', 1);
    const allResults: PatientHandoverItem[] = [...(firstPage.result || [])];

    // If first page returned fewer than 10 items, no more pages exist
    if (firstPage.result.length < 10) {
      return allResults;
    }

    const totalCount = firstPage.pageCount;
    let totalPages = 1;
    if (totalCount > 10) {
      totalPages = Math.ceil(totalCount / 10);
    }

    // If totalCount > 10, fetch pages 2..N concurrently (up to 25 pages / 250 patients)
    if (totalPages > 1) {
      const maxPages = Math.min(totalPages, 25);
      const pagePromises: Promise<{ result: PatientHandoverItem[]; pageCount: number }>[] = [];
      for (let p = 2; p <= maxPages; p++) {
        pagePromises.push(
          this.fetchHandedToMe('NTC0001', p).catch((err) => {
            console.warn(`Failed to fetch pending page ${p}:`, err);
            return { result: [], pageCount: 0 };
          })
        );
      }

      const additionalPages = await Promise.all(pagePromises);
      for (const pg of additionalPages) {
        if (pg.result && Array.isArray(pg.result)) {
          allResults.push(...pg.result);
        }
      }
    } else {
      // Fallback: If pageCount was not > 10, but first page had 10 items,
      // probe pages sequentially until empty or < 10
      let curPage = 2;
      while (curPage <= 15) {
        try {
          const nextPage = await this.fetchHandedToMe('NTC0001', curPage);
          if (!nextPage.result || nextPage.result.length === 0) break;
          allResults.push(...nextPage.result);
          if (nextPage.result.length < 10) break;
          curPage++;
        } catch {
          break;
        }
      }
    }

    // Deduplicate by hospitalNumber + ipNumber
    const seen = new Set<string>();
    const uniqueResults: PatientHandoverItem[] = [];
    for (const item of allResults) {
      const key = `${item.hospitalNumber}_${item.ipNumber}`;
      if (!seen.has(key)) {
        seen.add(key);
        uniqueResults.push(item);
      }
    }

    return uniqueResults;
  }

  /**
   * Fetch confirmed / accepted handover count (pageCount only) from ackStatus: 'NTC0002'
   */
  public async fetchConfirmedHandoverCount(): Promise<number> {
    const data = await this.fetchHandedToMe('NTC0002', 1);
    return data.pageCount;
  }

  /**
   * Fetch confirmed / accepted handovers (ackStatus: 'NTC0002')
   */
  public async fetchConfirmedHandovers(page = 1): Promise<{ result: PatientHandoverItem[]; pageCount: number }> {
    return this.fetchHandedToMe('NTC0002', page);
  }

  /**
   * Fetch handover visit details for a specific patient
   */
  public async fetchHandover(hospitalNumber: string, ipNumber: string): Promise<HandoverNote[]> {
    if (!this.loginId) {
      throw new Error('Not authenticated');
    }

    const payload = {
      organizationId: 1,
      businessId: 1,
      groupId: 1,
      hospitalNumber,
      ipNumber,
      userId: this.loginId,
      role: 'OTHERS',
      opOrIp: 'IP',
    };

    const endpoint = '/api-v2/doctor-desk-mobile/doctor-visit-details';

    const { status, data } = await this._post(endpoint, payload);

    if (status !== 200) {
      throw new Error(data?.message || `Failed to fetch visit details for ${hospitalNumber}`);
    }

    if (data?.error === false && data?.message === 'Fetched') {
      const visitGrouped = data.visitGroupedArray;
      const visitList: HandoverNote[] = [];

      if (Array.isArray(visitGrouped)) {
        for (const group of visitGrouped) {
          if (Array.isArray(group?.visitList)) {
            visitList.push(...group.visitList);
          }
        }
      } else if (visitGrouped && Array.isArray(visitGrouped.visitList)) {
        visitList.push(...visitGrouped.visitList);
      }

      // Automatically extract and store assigned nurse/user name if present
      if (!this.userName) {
        for (const note of visitList) {
          if (
            note.handeOverAssignedUserName &&
            (!note.handoverTo || String(note.handoverTo) === String(this.loginId))
          ) {
            this.setUserName(String(note.handeOverAssignedUserName));
            break;
          }
        }
      }

      return visitList;
    }

    return [];
  }

  /**
   * Accept / Update handover note
   */
  public async updateHandover(
    noteCode: string,
    hospitalId: string,
    ipNumber: string,
    reportKey: string,
    notes = '<p>Accepted</p>'
  ): Promise<boolean> {
    if (!this.loginId) {
      throw new Error('Not authenticated');
    }

    const payload = {
      noteCode,
      organizationId: 1,
      businessId: 1,
      groupId: 1,
      hospitalNumber: hospitalId,
      ipNumber,
      noteContent: '',
      assignedUserId: this.loginId,
      userId: this.loginId,
      reportKey,
      dischargeResultId: '',
      takeOverNotes: notes,
    };

    const endpoint = '/api-v2/doctor-desk-mobile/create-update-doctor-notes';

    const { status, data } = await this._post(endpoint, payload);

    if (status !== 200) {
      return false;
    }

    return data?.message === 'Saved Successfully!';
  }

  /**
   * Fetch inpatient list using ward-service-patient-list endpoint
   * Route: /api-v2/ward-service-patient-list
   * Accepts optional wardType, roomType, specialty, doctorCode, myPatient, and pagination.
   */
  public async fetchWardServicePatientList(
    filters?: Partial<WardServicePatientListFilter>
  ): Promise<{ result: ActiveInpatient[]; pageCount: number; totalCount: number }> {
    const page = filters?.page || 1;
    const payload = {
      organizationId: filters?.organizationId ?? 1,
      groupId: filters?.groupId ?? 1,
      businessId: filters?.businessId ?? 1,
      roomType: filters?.roomType ?? null,
      wardType: filters?.wardType ?? null,
      dischargedUnderSpecialty: filters?.dischargedUnderSpecialty ?? null,
      page,
      myPatient: filters?.myPatient ?? 0,
      doctorCode: filters?.doctorCode ?? null,
      nurseUserId: filters?.nurseUserId || this.loginId || 'VIMS5038',
      formCode: filters?.formCode || 'PG0029',
      arrivalStatus: filters?.arrivalStatus !== undefined ? filters.arrivalStatus : 1,
      ...(filters?.patientName ? { patientName: filters.patientName } : {}),
      ...(filters?.hospitalNumber !== undefined && filters?.hospitalNumber !== null && String(filters.hospitalNumber).trim() !== ''
        ? { hospitalNumber: !isNaN(Number(filters.hospitalNumber)) ? Number(filters.hospitalNumber) : filters.hospitalNumber }
        : {}),
    };

    const endpoint = '/api-v2/ward-service-patient-list';

    try {
      const { status, data } = await this._post<any>(endpoint, payload);

      if (status !== 200 || data?.error) {
        throw new Error(data?.message || `Failed to fetch inpatient list: status ${status}`);
      }

      const rawResult = (data?.result as any[]) || [];
      const totalCount = typeof data?.count === 'number' ? data.count : rawResult.length;
      const pageSize = 10;
      const pageCount = Math.max(1, Math.ceil(totalCount / pageSize));

      const result: ActiveInpatient[] = rawResult.map((p) => {
        const cleanDoctorName = (p.doctorName || p.DoctorName || '')
          .replace(/\s+undefined\b/gi, '')
          .trim();
        const pName = (p.patientName || p.displayName || p.firstName || '').trim();
        const bedNum = p.bedNumber || p.admittedBedNumber || '';
        const dept = p.department || p.dischargedepartmnt || p.departmentName || p.specialtyCode || '';
        const wardName = p.wardTypeName || p.wardType || '';
        const roomName = p.roomTypeName || p.roomType || '';

        return {
          ...p,
          patientName: pName,
          displayName: pName,
          bedNumber: bedNum,
          admittedBedNumber: bedNum,
          doctorName: cleanDoctorName,
          department: dept,
          departmentName: dept,
          wardType: wardName,
          wardTypeName: wardName,
          wardTypeNo: p.wardType,
          roomType: roomName,
          roomTypeName: roomName,
          roomTypeNo: p.roomType,
          gender: p.gender || p.genderName || '',
          genderName: p.gender || p.genderName || '',
          arrivalStatus: p.arrivalStatus !== undefined ? p.arrivalStatus : 0,
        };
      });

      return { result, pageCount, totalCount };
    } catch (err: any) {
      console.error('Failed to fetch inpatient list from ward-service-patient-list:', err);
      throw err;
    }
  }

  /**
   * Fetch active ongoing inpatients from hospital management system (backed by ward-service-patient-list)
   */
  public async fetchActiveInpatients(
    page = 1,
    filtersOrArrivalStatus?: Partial<WardServicePatientListFilter> | number
  ): Promise<{ result: ActiveInpatient[]; pageCount: number; totalCount: number }> {
    const filters: Partial<WardServicePatientListFilter> =
      typeof filtersOrArrivalStatus === 'object' && filtersOrArrivalStatus !== null
        ? { ...filtersOrArrivalStatus, page }
        : { page };

    return this.fetchWardServicePatientList(filters);
  }

  /**
   * Fetch all ongoing inpatients matching filters across all pages.
   * Only used when filters (department/ward/room) are active to avoid pulling all 800+ patients.
   */
  public async fetchAllFilteredInpatients(
    filters: Partial<WardServicePatientListFilter>
  ): Promise<{ result: ActiveInpatient[]; pageCount: number; totalCount: number }> {
    const firstPage = await this.fetchWardServicePatientList({ ...filters, page: 1 });
    const pageCount = firstPage.pageCount || 1;
    const totalCount = firstPage.totalCount || firstPage.result.length;

    if (pageCount <= 1 || firstPage.result.length === 0) {
      return { result: firstPage.result, pageCount: 1, totalCount };
    }

    // Cap at 15 pages (150 patients max for filtered list)
    const maxPages = Math.min(pageCount, 15);
    const pagePromises: Promise<{ result: ActiveInpatient[]; pageCount: number; totalCount: number }>[] = [];
    for (let p = 2; p <= maxPages; p++) {
      pagePromises.push(this.fetchWardServicePatientList({ ...filters, page: p }));
    }

    const remainingPages = await Promise.all(pagePromises);
    const combined: ActiveInpatient[] = [...firstPage.result];
    const seen = new Set(firstPage.result.map((p) => `${p.hospitalNumber}-${p.ipNumber}`));

    for (const pageRes of remainingPages) {
      for (const p of pageRes.result) {
        const key = `${p.hospitalNumber}-${p.ipNumber}`;
        if (!seen.has(key)) {
          seen.add(key);
          combined.push(p);
        }
      }
    }

    return { result: combined, pageCount: 1, totalCount: combined.length };
  }

  private cachedDepartments: DepartmentItem[] | null = null;

  /**
   * Fetch hospital departments list with caching
   */
  public async fetchDepartments(): Promise<DepartmentItem[]> {
    if (this.cachedDepartments && this.cachedDepartments.length > 0) {
      return this.cachedDepartments;
    }

    const defaultClinicalDepartments = [
      'ANESTHESIA',
      'CARDIOLOGY',
      'CRITICAL CARE MEDICINE',
      'EMERGENCY MEDICINE',
      'GENERAL MEDICINE',
      'GENERAL SURGERY',
      'INFECTIOUS DISEASE',
      'INTENSIVE CARE MEDICINE',
      'MEDICAL ONCOLOGY',
      'NEO NATOLOGY',
      'NEURO SURGERY',
      'NEUROLOGY',
      'NURSING',
      'OBSTETRICS AND GYNAECOLOGY',
      'OPHTHALMOLOGY',
      'ORTHOPAEDICS',
      'OTORHINOLARYNGOLOGY (ENT)',
      'PAEDIATRICS',
      'PAIN AND PALLIATIVE MEDICINE',
      'PALLIVATIVE MEDICINE',
      'PLASTIC SURGERY',
      'PULMONOLOGY',
      'RADIOLOGY',
      'UROLOGY'
    ];

    try {
      const { status, data } = await this._post('/api-v2/list-department-with-filter', { page: 1 });
      if (status === 200 && data?.result && Array.isArray(data.result)) {
        const rawList = data.result as DepartmentItem[];
        const validList = rawList
          .filter((d) => d && d.departmentName && d.departmentName.trim())
          .map((d) => ({
            id: d.id,
            departmentName: d.departmentName.trim().toUpperCase(),
            departmentCode: d.departmentCode,
            departmentType: d.departmentType,
            specialtyType: d.specialtyType,
          }));

        // Merge default list if some common ones weren't returned on page 1
        const seen = new Set(validList.map((d) => d.departmentName));
        for (const defName of defaultClinicalDepartments) {
          if (!seen.has(defName)) {
            seen.add(defName);
            validList.push({
              id: 0,
              departmentName: defName,
              departmentCode: undefined,
              departmentType: undefined,
              specialtyType: undefined,
            });
          }
        }

        validList.sort((a, b) => a.departmentName.localeCompare(b.departmentName));
        this.cachedDepartments = validList;
        return validList;
      }
    } catch (e) {
      console.warn('Could not fetch departments from server, using fallback list', e);
    }

    const fallbackList: DepartmentItem[] = defaultClinicalDepartments.map((name, idx) => ({
      id: idx + 1,
      departmentName: name,
    }));
    this.cachedDepartments = fallbackList;
    return fallbackList;
  }

  /**
   * Search doctors by name to retrieve their doctorCode
   */
  public async searchDoctors(searchQuery: string): Promise<Array<{ doctorCode?: string; userName?: string }>> {
    const q = searchQuery.replace(/^dr\.?\s+/i, '').trim();
    if (!q) return [];

    try {
      const fetchDocPage = (payload: Record<string, unknown>) =>
        this._post<any>('/api-v2/list/of/user', {
          organizationId: 1,
          businessId: 1,
          groupId: 1,
          limit: 25,
          userType: 21792, // DOCTOR
          ...payload,
        }).catch(() => ({ status: 0, data: {} }));

      // Fetch first 2 pages for firstName and userName in parallel to catch senior consultants/professors
      const pages = await Promise.all([
        fetchDocPage({ firstName: q, page: 1 }),
        fetchDocPage({ firstName: q, page: 2 }),
        fetchDocPage({ userName: q, page: 1 }),
        fetchDocPage({ userName: q, page: 2 }),
      ]);

      const list: any[] = [];
      for (const p of pages) {
        if (p.status === 200 && Array.isArray(p.data?.result)) {
          list.push(...p.data.result);
        }
      }

      // Prioritize active, non-deleted senior doctors
      list.sort((a, b) => {
        const aActive = (a.isActive === 1 && a.isDeleted === 0) ? 1 : 0;
        const bActive = (b.isActive === 1 && b.isDeleted === 0) ? 1 : 0;
        return bActive - aActive;
      });

      const qLower = q.toLowerCase();
      const seenCodes = new Set<string>();
      return list
        .filter((d) => {
          if (!d || !d.doctorCode) return false;
          if (seenCodes.has(d.doctorCode)) return false;
          const name = `${d.firstName || ''} ${d.userName || ''} ${d.lastName || ''}`.toLowerCase();
          if (name.includes(qLower)) {
            seenCodes.add(d.doctorCode);
            return true;
          }
          return false;
        })
        .map((d) => ({
          doctorCode: d.doctorCode,
          userName: d.userName || d.firstName || '',
        }));
    } catch {
      return [];
    }
  }

  /**
   * Search active inpatients across the hospital directory by patient name,
   * hospital number, or doctor name. Strict exact matching only.
   */
  public async searchActiveInpatients(
    query: string,
    filters?: Partial<WardServicePatientListFilter>
  ): Promise<{ result: ActiveInpatient[]; totalCount: number }> {
    const q = query.trim();
    if (!q) {
      const def = await this.fetchActiveInpatients(1, filters);
      return { result: def.result, totalCount: def.totalCount };
    }

    const isNumeric = /^\d+$/.test(q);
    const resultsMap = new Map<string, ActiveInpatient>();

    const baseFilter: Partial<WardServicePatientListFilter> = {
      arrivalStatus: 1,
      myPatient: filters?.myPatient ?? 0,
      dischargedUnderSpecialty: filters?.dischargedUnderSpecialty ?? null,
      wardType: filters?.wardType ?? null,
      roomType: filters?.roomType ?? null,
    };

    if (isNumeric) {
      try {
        const byHosp = await this.fetchWardServicePatientList({
          ...baseFilter,
          hospitalNumber: Number(q) || q,
          page: 1,
        });
        byHosp.result.forEach((p) => {
          const key = `${p.hospitalNumber}_${p.ipNumber}`;
          resultsMap.set(key, p);
        });
      } catch (e) {
        console.warn('Hospital number search error:', e);
      }
    } else {
      const searchTasks: Promise<void>[] = [];

      // 1. Search by patient name
      searchTasks.push(
        (async () => {
          try {
            const byName = await this.fetchWardServicePatientList({
              ...baseFilter,
              patientName: q,
              page: 1,
            });
            byName.result.forEach((p) => {
              const key = `${p.hospitalNumber}_${p.ipNumber}`;
              resultsMap.set(key, p);
            });
          } catch (e) {
            console.warn('Patient name search error:', e);
          }
        })()
      );

      // 2. Search by doctor name
      const cleanDocQuery = q.replace(/^dr\.?\s+/i, '').trim();
      if (cleanDocQuery.length >= 2) {
        searchTasks.push(
          (async () => {
            try {
              const doctors = await this.searchDoctors(cleanDocQuery);
              const doctorCodes = doctors
                .map((d) => d.doctorCode)
                .filter(Boolean)
                .slice(0, 10) as string[];

              if (doctorCodes.length > 0) {
                const docPatientPromises = doctorCodes.map((code) =>
                  this.fetchWardServicePatientList({
                    ...baseFilter,
                    doctorCode: code,
                    page: 1,
                  }).catch(() => ({ result: [] as ActiveInpatient[], pageCount: 0, totalCount: 0 }))
                );

                const docPatients = await Promise.all(docPatientPromises);
                docPatients.forEach((resp) => {
                  resp.result.forEach((p) => {
                    const key = `${p.hospitalNumber}_${p.ipNumber}`;
                    resultsMap.set(key, p);
                  });
                });
              }
            } catch (e) {
              console.warn('Doctor search error:', e);
            }
          })()
        );
      }

      await Promise.all(searchTasks);
    }

    // Strict exact matching filter:
    // Only return records where query is contained in patient name, hospital number, or doctor name.
    const qLower = q.toLowerCase();
    const cleanDocQLower = q.replace(/^dr\.?\s+/i, '').toLowerCase();

    const filtered = Array.from(resultsMap.values()).filter((p) => {
      const pName = (p.patientName || p.displayName || p.firstName || '').toLowerCase();
      const hosp = String(p.hospitalNumber || '').toLowerCase();
      const doc = (p.doctorName || p.DoctorName || '').toLowerCase();

      const nameMatch = pName.includes(qLower);
      const hospMatch = hosp.includes(qLower);
      const docMatch = doc.includes(qLower) || (cleanDocQLower ? doc.includes(cleanDocQLower) : false);

      return nameMatch || hospMatch || docMatch;
    });

    return {
      result: filtered,
      totalCount: filtered.length,
    };
  }

  /**
   * Search hospital nursing staff and users.
   * Concurrent multi-field search (firstName, userName, token combinations) with strict exact name/ID filtering.
   */
  public async searchNurses(searchQuery?: string, page = 1, limit = 50): Promise<NurseUser[]> {
    const q = searchQuery?.trim() || '';

    // If query is empty, return initial nursing staff list
    if (!q) {
      const payload: Record<string, unknown> = {
        organizationId: 1,
        businessId: 1,
        groupId: 1,
        page,
        limit,
        userType: 21793, // Nursing staff
      };

      try {
        const { status, data } = await this._post('/api-v2/list/of/user', payload);
        if (status === 200 && data?.result && Array.isArray(data.result)) {
          return this._mapToNurseUsers(data.result);
        }
      } catch (err) {
        console.error('Failed to load initial nurses:', err);
      }
      return [];
    }

    // Query is provided: build parallel requests for firstName, userName, and composite tokens
    const tokens = q.split(/\s+/).filter(Boolean);
    const requests: Promise<{ status: number; data: any }>[] = [];

    const fetchUsers = (extraPayload: Record<string, unknown>) => {
      return this._post<any>('/api-v2/list/of/user', {
        organizationId: 1,
        businessId: 1,
        groupId: 1,
        page: 1,
        limit: 40,
        userType: 21793,
        ...extraPayload,
      }).catch(() => ({ status: 0, data: {} }));
    };

    // 1. Search by firstName (matches first name column)
    requests.push(fetchUsers({ firstName: q }));

    // 2. Search by userName (matches lastName/secondName column)
    requests.push(fetchUsers({ userName: q }));

    // 3. Multi-token combinations
    if (tokens.length > 1) {
      requests.push(fetchUsers({ firstName: tokens[0], userName: tokens.slice(1).join(' ') }));
      requests.push(fetchUsers({ firstName: tokens[0] }));
      requests.push(fetchUsers({ userName: tokens[tokens.length - 1] }));
    }

    // 4. Also check without userType restriction if searching by ID/code (e.g. VIMS5038 or numbers)
    if (/^[a-zA-Z0-9]+$/i.test(q)) {
      requests.push(fetchUsers({ loginId: q }));
      requests.push(fetchUsers({ firstName: q, userType: undefined }));
    }

    try {
      const responses = await Promise.all(requests);
      const userMap = new Map<string | number, any>();

      for (const res of responses) {
        if (res.status === 200 && Array.isArray(res.data?.result)) {
          for (const u of res.data.result) {
            const key = u.loginId || u.id;
            if (key && !userMap.has(key)) {
              userMap.set(key, u);
            }
          }
        }
      }

      const allUsers = this._mapToNurseUsers(Array.from(userMap.values()));

      // STRICT EXACT FILTERING:
      // Every search token must appear in user's name, login ID, or employee ID.
      // Eliminates unintended matches (such as email matches showing Rajibul when searching "Rahul").
      const filtered = allUsers.filter((u) => {
        const fullSearchable = `${u.userName} ${u.firstName || ''} ${u.SecondName || ''} ${u.lastName || ''} ${u.loginId} ${u.employeeId || ''} ${u.empId || ''} ${u.employeeCode || ''}`.toLowerCase();
        return tokens.every((tok) => fullSearchable.includes(tok.toLowerCase()));
      });

      return filtered;
    } catch (err) {
      console.error('Failed to search nurses:', err);
      return [];
    }
  }

  private _mapToNurseUsers(rawUsers: any[]): NurseUser[] {
    return rawUsers
      .filter((u) => u && (u.userName || u.firstName || u.loginId))
      .map((u) => {
        const empId = u.employeeId ?? u.empId ?? u.employId ?? u.employid ?? u.id;
        const displayName = (u.userName || `${u.firstName || ''} ${u.SecondName || u.lastName || ''}`).trim();
        return {
          ...u,
          id: u.id,
          userName: displayName || u.loginId || '',
          loginId: u.loginId || '',
          employeeCode: u.employeeCode,
          employeeId: empId ? String(empId) : undefined,
          empId: empId ? String(empId) : undefined,
        };
      })
      .filter((u) => Boolean(u.userName && u.loginId));
  }

  /**
   * Cleans ISBAR HTML by eliminating all invalid ":" characters between cells/labels
   */
  public cleanIsbarContent(raw: string): string {
    if (!raw || typeof raw !== 'string') return '';
    let html = raw.trim();

    // Strip outer table tags temporarily if present so we operate on rows
    html = html.replace(/^<table[^>]*>\s*(?:<tbody[^>]*>)?/i, '');
    html = html.replace(/(?:<\/tbody>\s*)?<\/table>\s*$/i, '');

    // Remove colons between table cells e.g. </td> : <td>
    html = html.replace(/<\/td>\s*:\s*<td>/gi, '</td><td>');
    // Remove colons after bold/strong labels e.g. <b>Label</b> : or <strong>Label</strong> :
    html = html.replace(/(<(?:b|strong)[^>]*>[^<]*<\/(?:b|strong)>)\s*:\s*/gi, '$1 ');
    // Remove colons preceding closing tags e.g. :</p> or :</td>
    html = html.replace(/:\s*(<\/(?:p|td|div|b|strong)>)/gi, '$1');
    // Remove any remaining stray colons near cell boundaries
    html = html.replace(/<\/td>\s*:\s*/gi, '</td>');
    html = html.replace(/:\s*<td/gi, '<td');
    // Remove stray <br> tags between </tr> and <tr>
    html = html.replace(/<\/tr>\s*<br\s*\/?>\s*(<tr|<\/tbody|<\/table|$)/gi, '</tr>$1');

    html = html.trim();

    // Re-wrap in table if rows remain
    if (html.includes('<tr>')) {
      if (!html.startsWith('<table')) {
        html = `<table><tbody>${html}`;
      }
      if (!html.endsWith('</table>')) {
        if (!html.endsWith('</tbody>')) {
          html += '</tbody>';
        }
        html += '</table>';
      }
    }

    return html;
  }

  /**
   * Fetch patient ISBAR handover detail HTML
   * Route: notify/isbhar-detail
   */
  public async fetchIsbarDetail(
    hospitalNumber: string | number,
    ipNumber: string | number
  ): Promise<string> {
    const payload = {
      p_organizationId: 1,
      p_groupId: 1,
      p_businessId: 1,
      p_hospitalNumber: Number(hospitalNumber),
      p_ipNumber: Number(ipNumber),
    };

    const endpoint = '/api-v2/notify/isbhar-detail';

    try {
      const { status, data } = await this._post<{ error: boolean; result?: string }>(endpoint, payload);
      if (status === 200 && data && data.error === false && typeof data.result === 'string') {
        const raw = data.result.trim();
        return this.cleanIsbarContent(raw);
      }
    } catch (err) {
      console.warn(`Failed to fetch ISBAR detail for ${hospitalNumber}/${ipNumber}:`, err);
    }
    return '';
  }

  /**
   * Give / transfer handover of a patient to a specific nurse
   * Uses notify/isbhar-detail to fetch clinical handover note details for submission
   */
  public async giveHandover(params: {
    hospitalNumber: string | number;
    ipNumber: string | number;
    targetNurseLoginId: string;
    targetNurseName: string;
    targetNurseEmpId?: string | number;
    targetNurse?: NurseUser;
    remarks?: string;
    patient?: PatientHandoverItem | ActiveInpatient;
    isbarContent?: string;
  }): Promise<{ success: boolean; message: string; reportKey?: string }> {
    if (!this.loginId) {
      throw new Error('Not authenticated');
    }

    const { hospitalNumber, ipNumber, targetNurseLoginId, targetNurseName, remarks, patient, isbarContent } = params;

    // 1. Fetch patient ISBAR handover detail via notify/isbhar-detail route
    let isbarDetails = (isbarContent || '').trim();
    if (!isbarDetails) {
      try {
        isbarDetails = await this.fetchIsbarDetail(hospitalNumber, ipNumber);
      } catch (err) {
        console.warn('Could not fetch ISBAR detail for patient:', err);
      }
    }

    // 2. Build complete noteContent with ISBAR details
    const handoverSummary = `Handed over to ${targetNurseName}`;

    // If ISBAR detail is empty or minimal, fallback to constructing basic patient profile
    if (!isbarDetails || isbarDetails === '<table><tbody></tbody></table>' || isbarDetails === '</tbody></table>') {
      const p = patient;
      const pName = (p?.patientName || (p as ActiveInpatient)?.displayName || (p as ActiveInpatient)?.firstName || '').trim();
      const age = (p as ActiveInpatient)?.age || '';
      const doa = (p as ActiveInpatient)?.effectiveDateOfAdmission || p?.doa || '';
      const doc = (p as ActiveInpatient)?.doctorName || (p as ActiveInpatient)?.DoctorName || p?.doctorName || '';
      const dept = p?.departmentName || (p as ActiveInpatient)?.specialtyCodeValue || p?.department || '';
      const unit = (p as ActiveInpatient)?.deparmentUnit || p?.departmentUnit || '1';

      isbarDetails = `<p><strong>Hospital Number</strong> ${hospitalNumber}<br>` +
        `<strong>Ip Number</strong> ${ipNumber}<br>` +
        (pName ? `<strong>Patient Name</strong> ${pName}&nbsp;<br>` : '') +
        (age ? `<strong>Age Yy</strong> ${age}<br>` : '') +
        (doa ? `<strong>DOA</strong> ${doa}<br>` : '') +
        (doc ? `<strong>Attending Psycian</strong> ${doc}<br>` : '') +
        (dept ? `<strong>Department Name</strong> ${dept}<br>` : '') +
        (unit ? `<strong>Deparment Unit</strong> ${unit}<br>` : '') +
        `<strong>Presentcomplain</strong></p>`;
    } else {
      isbarDetails = this.cleanIsbarContent(isbarDetails);
    }

    let finalNoteContent = isbarDetails;
    if (remarks && remarks.trim()) {
      finalNoteContent += `<p><br><strong>Handover Note</strong> : ${remarks.trim()}</p><p>${handoverSummary}</p>`;
    } else {
      finalNoteContent += `<p><br>${handoverSummary}</p>`;
    }

    // 3. Exact payload matching route: doctor-desk-mobile/create-update-doctor-notes
    const payload: Record<string, unknown> = {
      noteCode: 'TPTY0026',
      organizationId: 1,
      businessId: 1,
      groupId: 1,
      hospitalNumber: Number(hospitalNumber),
      ipNumber: Number(ipNumber),
      handOverTo: targetNurseLoginId,
      handoverTo: targetNurseLoginId,
      handOverToDescription: targetNurseName,
      assignedUserId: targetNurseLoginId,
      loginId: this.loginId,
      userId: this.loginId,
      dischargeResultId: '',
      noteContent: finalNoteContent,
    };

    const endpoint = '/api-v2/doctor-desk-mobile/create-update-doctor-notes';
    const { status, data } = await this._post(endpoint, payload);

    if (status !== 200) {
      throw new Error(data?.message || `Failed to submit handover: status ${status}`);
    }

    if (data?.error === false) {
      return {
        success: true,
        message: data.message || 'Handover successfully assigned to nurse',
        reportKey: data.reportKey,
      };
    }

    return {
      success: false,
      message: data?.message || 'Failed to submit handover note',
    };
  }
}


export const api = new VykoAPIClient();
